import { formatTaka } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Spinner, cn } from "@brandex/ui";
import { useBrandex } from "@brandex/dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { commerceKeys, useComponents, useIsSuperAdmin, usePlans, usePricing, type AdminPlan, type Component, type PlanItem, type Quote } from "../data";
import { fieldError, QueryError } from "../views";

const taka = (paisa: number) => formatTaka("en", paisa);
const toPaisa = (takaText: string) => Math.round(Number(takaText.replace(/[^\d.]/g, "")) * 100);

/** Packages: component catalogue, plans (pin/archive), quote builder, CAC (F-04). */
export function PackagesPage() {
  const components = useComponents();
  const plans = usePlans();
  if (components.isPending || plans.isPending) return <Spinner label="Loading…" />;
  if (components.isError) return <QueryError error={components.error} />;
  if (plans.isError) return <QueryError error={plans.error} />;
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Packages</h1>
        <p className="mt-1 text-sm text-muted-foreground">Component catalogue, plans and quotes. The pinned plan appears on the public site.</p>
      </header>
      <PlansSection plans={plans.data} components={components.data} />
      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <CatalogueSection components={components.data} />
        <div className="grid content-start gap-6">
          <QuoteBuilder components={components.data} />
          <PricingSettingsCard />
        </div>
      </div>
    </div>
  );
}

function PlansSection({ plans, components }: { plans: AdminPlan[]; components: Component[] }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const act = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "pin" | "archive" }) => {
      if (action === "pin") await api.http.POST("/v1/admin/plans/{plan_id}/pin", { params: { path: { plan_id: id } } });
      else await api.http.POST("/v1/admin/plans/{plan_id}/archive", { params: { path: { plan_id: id } } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commerceKeys.plans }),
  });
  return (
    <section aria-labelledby="plans-title" className="grid gap-3">
      <div className="flex items-center justify-between">
        <h2 id="plans-title" className="font-display text-lg font-semibold">Plans</h2>
        <Button size="sm" variant="outline" onClick={() => setCreating((v) => !v)}>{creating ? "Close" : "New plan"}</Button>
      </div>
      {act.isError ? <QueryError error={act.error} /> : null}
      {creating ? <PlanForm components={components} onDone={() => setCreating(false)} /> : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {plans.map((p) => (
          <Card key={p.id} className={cn(p.pinned && "ring-2 ring-azure-500")}>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-lg">{p.name.en}</CardTitle>
                <div className="flex gap-1">
                  {p.pinned ? <Badge tone="accent"><Pin className="mr-1 h-3 w-3" aria-hidden />Pinned</Badge> : null}
                  <Badge tone={p.includes_website ? "success" : "neutral"}>{p.includes_website ? "Website" : "No website"}</Badge>
                  {p.status === "archived" ? <Badge>Archived</Badge> : null}
                </div>
              </div>
              <CardDescription>/{p.slug}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <p className="font-display text-3xl font-bold">{taka(p.monthly_price_paisa)}<span className="text-sm font-normal text-muted-foreground"> / month</span></p>
              <dl className="grid grid-cols-2 gap-y-1">
                <dt className="text-muted-foreground">Market value</dt><dd className="text-right tabular-nums">{taka(p.market_value_paisa)}</dd>
                <dt className="text-muted-foreground">Internal cost</dt><dd className="text-right tabular-nums">{taka(p.quote.internal_cost_paisa)}</dd>
                <dt className="text-muted-foreground">Margin after CAC</dt>
                <dd className={cn("text-right tabular-nums", p.quote.margin_paisa < 0 && "text-destructive")}>{taka(p.quote.margin_paisa)}{p.quote.margin_bps !== null && p.quote.margin_bps !== undefined ? ` (${(p.quote.margin_bps / 100).toFixed(0)}%)` : ""}</dd>
              </dl>
              <ul className="text-xs text-muted-foreground">
                {p.lines.filter((l) => !l.always_included).map((l) => <li key={l.key}>{l.unit === "per_item" ? `${l.quantity} × ` : "+ "}{l.name.en}</li>)}
              </ul>
              {p.status === "active" ? (
                <div className="flex gap-2">
                  {!p.pinned ? <Button size="sm" loading={act.isPending && act.variables.id === p.id && act.variables.action === "pin"} onClick={() => act.mutate({ id: p.id, action: "pin" })}>Pin to site</Button> : null}
                  <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: p.id, action: "archive" })}>Archive</Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function PlanForm({ components, onDone }: { components: Component[]; onDone: () => void }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const perItem = components.filter((c) => c.unit === "per_item" && c.active);
  const addOns = components.filter(isAddOn);
  const [slug, setSlug] = useState("");
  const [withAddOns, setWithAddOns] = useState<string[]>(() => addOns.map((c) => c.id));
  const [name, setName] = useState("");
  const [nameBn, setNameBn] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: async () => {
      const items: PlanItem[] = [
        ...perItem.flatMap((c) => {
          const q = Number(qty[c.id] ?? "0");
          return q > 0 ? [{ component_id: c.id, quantity: q }] : [];
        }),
        ...withAddOns.map((id) => ({ component_id: id, quantity: 1 })),
      ];
      await api.http.POST("/v1/admin/plans", {
        body: { slug, name: { en: name, bn: nameBn }, items, monthly_price_paisa: toPaisa(price) },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commerceKeys.plans });
      onDone();
    },
  });
  return (
    <Card>
      <CardContent className="grid gap-4 pt-6">
        {create.isError ? <QueryError error={create.error} /> : null}
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Slug"><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="premium" /></Field>
          <Field label="Name (English)"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Name (Bangla)"><Input value={nameBn} onChange={(e) => setNameBn(e.target.value)} /></Field>
          <Field label="Monthly price (৳)"><Input inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
          {perItem.map((c) => (
            <Field key={c.id} label={c.name.en}>
              <Input inputMode="numeric" value={qty[c.id] ?? ""} onChange={(e) => setQty({ ...qty, [c.id]: e.target.value.replace(/\D/g, "") })} placeholder="0" />
            </Field>
          ))}
        </div>
        <AddOnPicker addOns={addOns} selected={withAddOns} onChange={setWithAddOns} />
        <p className="text-xs text-muted-foreground">Core monthly services are in every plan. Add-ons such as the doctor website are only in plans that include them — only those clients get a website.</p>
        <div><Button loading={create.isPending} onClick={() => create.mutate()}>Create plan</Button></div>
      </CardContent>
    </Card>
  );
}

function CatalogueSection({ components }: { components: Component[] }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ cost: "", value: "", active: true, alwaysIncluded: true });
  const save = useMutation({
    mutationFn: async (c: Component) => {
      await api.http.PUT("/v1/admin/package-components/{component_id}", {
        params: { path: { component_id: c.id } },
        body: { key: c.key, name: c.name, unit: c.unit, unit_cost_paisa: toPaisa(draft.cost), unit_value_paisa: toPaisa(draft.value), active: draft.active, always_included: draft.alwaysIncluded, sort_order: c.sort_order },
      });
    },
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: commerceKeys.components });
      void qc.invalidateQueries({ queryKey: commerceKeys.plans });
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Component catalogue</CardTitle>
        <CardDescription>Cost and market value per unit (per item, or per month for monthly services).</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {save.isError ? <QueryError error={save.error} /> : null}
        <table className="w-full min-w-[560px] text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="py-2">Component</th><th>Unit</th><th className="text-right">Cost</th><th className="text-right">Value</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {components.map((c) => (
              <tr key={c.id} className={cn(!c.active && "opacity-50")}>
                <td className="py-2"><span className="font-medium">{c.name.en}</span> <code className="text-xs text-muted-foreground">{c.key}</code></td>
                <td className="text-muted-foreground">{c.unit === "per_item" ? "per item" : c.always_included ? "monthly · every package" : "monthly · add-on"}</td>
                {editing === c.id ? (
                  <>
                    <td className="text-right"><Input aria-label="Cost (৳)" className="h-8 w-24 text-right" value={draft.cost} onChange={(e) => setDraft({ ...draft, cost: e.target.value })} /></td>
                    <td className="text-right"><Input aria-label="Value (৳)" className="h-8 w-24 text-right" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} /></td>
                    <td className="whitespace-nowrap pl-2 text-right">
                      <label className="mr-2 text-xs"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> active</label>
                      {c.unit === "monthly" ? (
                        <label className="mr-2 text-xs"><input type="checkbox" checked={draft.alwaysIncluded} onChange={(e) => setDraft({ ...draft, alwaysIncluded: e.target.checked })} /> every package</label>
                      ) : null}
                      <Button size="sm" loading={save.isPending} onClick={() => save.mutate(c)}>Save</Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="text-right tabular-nums">{taka(c.unit_cost_paisa)}</td>
                    <td className="text-right tabular-nums">{taka(c.unit_value_paisa)}</td>
                    <td className="pl-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(c.id); setDraft({ cost: String(c.unit_cost_paisa / 100), value: String(c.unit_value_paisa / 100), active: c.active, alwaysIncluded: c.always_included }); }}>Edit</Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <NewComponentForm />
      </CardContent>
    </Card>
  );
}

function NewComponentForm() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ key: "", en: "", bn: "", unit: "per_item" as "per_item" | "monthly", cost: "", value: "", alwaysIncluded: true });
  const create = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/package-components", {
        body: { key: f.key, name: { en: f.en, bn: f.bn }, unit: f.unit, unit_cost_paisa: toPaisa(f.cost), unit_value_paisa: toPaisa(f.value), always_included: f.unit === "monthly" && f.alwaysIncluded, sort_order: 100 },
      });
    },
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: commerceKeys.components });
    },
  });
  if (!open) return <Button className="mt-4" size="sm" variant="outline" onClick={() => setOpen(true)}>Add component</Button>;
  return (
    <div className="mt-4 grid gap-3 rounded-md border border-border p-4">
      {create.isError ? <QueryError error={create.error} /> : null}
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Key"><Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="blog-article" /></Field>
        <Field label="Name (English)"><Input value={f.en} onChange={(e) => setF({ ...f, en: e.target.value })} /></Field>
        <Field label="Name (Bangla)"><Input value={f.bn} onChange={(e) => setF({ ...f, bn: e.target.value })} /></Field>
        <Field label="Unit">
          <Select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value as "per_item" | "monthly" })}>
            <option value="per_item">Per item</option>
            <option value="monthly">Monthly service</option>
          </Select>
        </Field>
        <Field label="Cost per unit (৳)"><Input value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} /></Field>
        <Field label="Value per unit (৳)"><Input value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></Field>
      </div>
      {f.unit === "monthly" ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.alwaysIncluded} onChange={(e) => setF({ ...f, alwaysIncluded: e.target.checked })} />
          Included in every package (untick for an add-on that plans choose)
        </label>
      ) : null}
      <div className="flex gap-2">
        <Button size="sm" loading={create.isPending} onClick={() => create.mutate()}>Add</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

/** Live custom quote: quantities + price → cost, value, margin. */
export function QuoteBuilder({ components }: { components: Component[] }) {
  const { api } = useBrandex();
  const perItem = useMemo(() => components.filter((c) => c.unit === "per_item" && c.active), [components]);
  const addOns = useMemo(() => components.filter(isAddOn), [components]);
  const [withAddOns, setWithAddOns] = useState<string[]>([]);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [price, setPrice] = useState("40000");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    const items = [
      ...perItem.flatMap((c) => ((qty[c.id] ?? 0) > 0 ? [{ component_id: c.id, quantity: qty[c.id] ?? 0 }] : [])),
      ...withAddOns.map((id) => ({ component_id: id, quantity: 1 })),
    ];
    const timer = setTimeout(() => {
      api.http
        .POST("/v1/admin/quotes", { body: { items, price_paisa: toPaisa(price) || 0 } })
        .then(({ data }) => { setQuote(data ?? null); setError(null); })
        .catch((e: unknown) => setError(e));
    }, 250);
    return () => clearTimeout(timer);
  }, [api, perItem, qty, price, withAddOns]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom quote builder</CardTitle>
        <CardDescription>Core monthly services are always included; tick add-ons such as the website.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="grid grid-cols-2 gap-3">
          {perItem.map((c) => (
            <Field key={c.id} label={c.name.en}>
              <Input inputMode="numeric" value={qty[c.id] ?? 0} onChange={(e) => setQty({ ...qty, [c.id]: Number(e.target.value.replace(/\D/g, "")) })} />
            </Field>
          ))}
        </div>
        <AddOnPicker addOns={addOns} selected={withAddOns} onChange={setWithAddOns} />
        <Field label="Price (৳ / month)"><Input inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
        {error !== null ? <QueryError error={error} /> : null}
        {quote !== null ? (
          <dl className="grid grid-cols-2 gap-y-1 rounded-md bg-muted p-3 text-sm" aria-live="polite">
            <dt>Internal cost</dt><dd className="text-right tabular-nums">{taka(quote.internal_cost_paisa)}</dd>
            <dt>Market value</dt><dd className="text-right tabular-nums">{taka(quote.market_value_paisa)}</dd>
            <dt>Cost with CAC</dt><dd className="text-right tabular-nums">{taka(quote.cost_with_cac_paisa)}</dd>
            <dt className="font-semibold">Margin</dt>
            <dd className={cn("text-right font-semibold tabular-nums", quote.margin_paisa < 0 && "text-destructive")}>
              {taka(quote.margin_paisa)}{quote.margin_bps !== null && quote.margin_bps !== undefined ? ` · ${(quote.margin_bps / 100).toFixed(0)}%` : ""}
            </dd>
          </dl>
        ) : null}
        {quote?.below_floor ? (
          <Alert tone="error">Below the minimum margin. Only a Super admin can send this price, with a written reason.</Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Basis points → a percent string for the input ("1000" → "10"). */
function bpsToPercent(bps: number | null | undefined): string {
  return bps === null || bps === undefined ? "" : String(bps / 100);
}

function PricingSettingsCard() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const pricing = usePricing();
  const superAdmin = useIsSuperAdmin();
  const [cac, setCac] = useState<string | null>(null);
  const [floor, setFloor] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const current = pricing.data;
      await api.http.PUT("/v1/admin/pricing-settings", {
        body: {
          cac_paisa: cac === null ? (current?.cac_paisa ?? 0) : toPaisa(cac),
          ...(floor === null ? {} : { min_margin_bps: Math.round(Number(floor) * 100) }),
        },
      });
    },
    onSuccess: () => {
      setCac(null);
      setFloor(null);
      void qc.invalidateQueries({ queryKey: commerceKeys.pricing });
      void qc.invalidateQueries({ queryKey: commerceKeys.plans });
    },
  });
  if (pricing.isError) return <QueryError error={pricing.error} />;
  return (
    <Card>
      <CardHeader><CardTitle>Pricing settings</CardTitle></CardHeader>
      <CardContent className="grid gap-3">
        {save.isSuccess ? <Alert tone="success">Saved. Margins now use the new settings.</Alert> : null}
        {save.isError && fieldError(save.error, "min_margin_bps") === undefined ? <QueryError error={save.error} /> : null}
        <Field label="Customer acquisition cost per client (৳ / month)">
          <Input inputMode="numeric" value={cac ?? (pricing.data !== undefined ? String(pricing.data.cac_paisa / 100) : "")} onChange={(e) => setCac(e.target.value)} />
        </Field>
        <Field
          label="Minimum margin (%)"
          hint={superAdmin ? "Quotes below this need your approval and a reason." : "Only a Super admin can change the floor."}
          error={fieldError(save.error, "min_margin_bps")}
        >
          <Input
            inputMode="decimal"
            disabled={!superAdmin}
            value={floor ?? bpsToPercent(pricing.data?.min_margin_bps)}
            onChange={(e) => setFloor(e.target.value.replace(/[^\d.]/g, ""))}
          />
        </Field>
        <div><Button size="sm" disabled={cac === null && floor === null} loading={save.isPending} onClick={() => save.mutate()}>Save</Button></div>
      </CardContent>
    </Card>
  );
}

/** Monthly services that plans list explicitly (e.g. the doctor website). */
export function isAddOn(c: Component): boolean {
  return c.active && c.unit === "monthly" && !c.always_included;
}

function AddOnPicker({ addOns, selected, onChange }: { addOns: Component[]; selected: string[]; onChange: (ids: string[]) => void }) {
  if (addOns.length === 0) return null;
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1 text-sm font-medium">Add-ons</legend>
      {addOns.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={selected.includes(c.id)}
            onChange={(e) => onChange(e.target.checked ? [...selected, c.id] : selected.filter((id) => id !== c.id))}
          />
          {c.name.en} <span className="text-muted-foreground">({taka(c.unit_value_paisa)} value / month)</span>
        </label>
      ))}
    </fieldset>
  );
}
